import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Award, Heart, TrendingUp, Trophy } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import StatCard from "@/components/shared/StatCard";
import SectionCard from "@/components/shared/SectionCard";
import DataExportButton from "@/components/shared/DataExportButton";
import EmptyState from "@/components/shared/EmptyState";
import { SearchInput } from "@/components/shared/SearchInput";
import { TableSkeleton } from "@/components/shared/Skeletons";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAdminTips, DEMO_ADMIN_TIPS } from "@/hooks/useTips";
import { useAuthStore } from "@/store/authStore";

export const Route = createFileRoute("/admin/tips/")({
  component: AdminTipsPage,
});

const formatTZS = (n: number) =>
  new Intl.NumberFormat("en-TZ", {
    style: "currency",
    currency: "TZS",
    minimumFractionDigits: 0,
  }).format(n);

function AdminTipsPage() {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const { user } = useAuthStore();
  const { data: tips = [], isLoading } = useAdminTips();

  const displayTips = user?.isDemo ? DEMO_ADMIN_TIPS : tips;

  const filteredTips = displayTips.filter((t) => {
    const name = ((t.student_name || "") + " " + (t.partner_name || "")).toLowerCase();
    const matchesSearch = name.includes(search.toLowerCase());
    const matchesType = typeFilter === "all" || (t.type || "appreciation") === typeFilter;
    return matchesSearch && matchesType;
  });

  const totalTips = displayTips.length;
  const totalAmount = displayTips.reduce((sum: number, t) => sum + t.amount, 0);

  // Top tipper
  const tipByPartner: Record<number, { name: string; total: number }> = {};
  displayTips.forEach((t) => {
    if (!tipByPartner[t.partner_id]) {
      tipByPartner[t.partner_id] = { name: t.partner_name || "Partner", total: 0 };
    }
    tipByPartner[t.partner_id].total += t.amount;
  });
  const topTipper = Object.values(tipByPartner).sort((a, b) => b.total - a.total)[0];

  // Top recipient
  const tipByStudent: Record<number, { name: string; total: number }> = {};
  displayTips.forEach((t) => {
    if (!tipByStudent[t.student_id]) {
      tipByStudent[t.student_id] = { name: t.student_name || "Student", total: 0 };
    }
    tipByStudent[t.student_id].total += t.amount;
  });
  const topRecipient = Object.values(tipByStudent).sort((a, b) => b.total - a.total)[0];

  return (
    <div className="space-y-6 pb-4">
      <PageHeader
        title="Tips & motivation"
        subtitle="Monitor all tips sent by partners to students across the platform."
        breadcrumbs={[{ label: "Dashboard", path: "/admin/dashboard" }, { label: "Tips" }]}
        action={
          <DataExportButton
            onExport={(format) => console.log("Exporting tips as", format)}
            label="Export tips"
          />
        }
      />

      {/* Stats row */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-4">
        <StatCard label="Total tips" value={totalTips} icon={<Heart />} />
        <StatCard label="Total amount" value={formatTZS(totalAmount)} icon={<TrendingUp />} />
        <StatCard label="Top tipper" value={topTipper?.name || "—"} icon={<Award />} />
        <StatCard label="Top recipient" value={topRecipient?.name || "—"} icon={<Trophy />} />
      </div>

      {/* Tips table */}
      <SectionCard
        title="All tips"
        actions={
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search student or partner…"
              className="min-w-0 flex-1 sm:w-60 sm:flex-none"
            />
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-8 w-[140px]" aria-label="Tip type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="appreciation">Appreciation</SelectItem>
                <SelectItem value="milestone">Milestone</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
        noPadding
      >
        {isLoading ? (
          <TableSkeleton rows={5} columns={4} />
        ) : filteredTips.length === 0 ? (
          <EmptyState
            bare
            icon={<Award />}
            title="No tips found"
            description="No tips match your current filters. Try adjusting the search or filter criteria."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-4 md:pl-5">Student</TableHead>
                <TableHead>Partner</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="pr-4 md:pr-5">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTips.map((tip) => {
                const status = tip.status || "pending";
                return (
                  <TableRow key={tip.id} className="h-11 [&>td]:py-1.5">
                    <TableCell className="min-w-[160px] pl-4 md:pl-5">
                      <p className="font-medium text-foreground">{tip.student_name || "—"}</p>
                      {tip.student_email && (
                        <p className="text-caption text-muted-foreground">{tip.student_email}</p>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-body">
                      {tip.partner_name || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={tip.type === "milestone" ? "info" : "outline"}>
                        {tip.type === "milestone" ? "Milestone" : "Appreciation"}
                      </Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right font-mono text-foreground tabular-nums">
                      {formatTZS(tip.amount)}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate text-muted-foreground">
                      {tip.message || "—"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums">
                      {tip.created_at ? new Date(tip.created_at).toLocaleDateString("en-GB") : "—"}
                    </TableCell>
                    <TableCell className="pr-4 md:pr-5">
                      <Badge variant={status === "completed" ? "success" : "warning"} dot>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </SectionCard>
    </div>
  );
}
